import React, { Suspense } from 'react';
import { getVisibleSelectionRect } from 'draft-js';
import EditorUtils from '../../../draft-js-plugins-utils/src';
import {
  ItalicButton,
  BoldButton,
  UnderlineButton,
  CodeButton,
} from '../../../draft-js-buttons/src';
import { ToolbarProps } from '..';

import linkifyIt from 'linkify-it';
import tlds from 'tlds';
import LinkButton from '../../../draft-js-anchor-plugin/src/components/LinkButton';
import AddLinkForm, {
  RemoveBlock,
} from '../../../draft-js-anchor-plugin/src/components/AddLinkForm';
import ButtonBlockForm from '../../../draft-js-cta-plugin/components/CTAForm';
import { ENTITY_TYPE } from '../../../utils/types';
import ImageButton from '../../../draft-js-image-plugin/src/ImageButton';
import { isBomBombVideoSelected } from '../../../draft-js-bombbomb-plugin';

const BombBombVideoButton = React.lazy(
  () => import('../../../draft-js-bombbomb-plugin/BombBombButton')
);

const linkify = linkifyIt();
linkify.tlds(tlds);

const getRelativeParent = (element: any): any => {
  if (!element) {
    return null;
  }

  const position = window.getComputedStyle(element).getPropertyValue('position');
  if (position !== 'static') {
    return element;
  }

  return getRelativeParent(element.parentElement);
};

const ImageToolbar: React.FC<any> = (props) => {
  return (
    <>
      <LinkButton store={props} ownTheme={props.theme} title='Edit link' {...props} />
      <ImageButton store={props} ownTheme={props.theme} title='Edit image' {...props} />
      <RemoveBlock isEntireBlock title='Delete' {...props} />
    </>
  );
};

const BombBombToolbar: React.FC<any> = (props) => {
  return (
    <>
      <Suspense>
        <BombBombVideoButton store={props} ownTheme={props.theme} {...props} />
      </Suspense>
      <RemoveBlock isEntireBlock {...props} />
    </>
  );
};

export default class Toolbar extends React.Component<ToolbarProps> {
  static defaultProps = {
    children: (externalProps: any): JSX.Element => (
      <React.Fragment>
        <ItalicButton {...externalProps} />
        <BoldButton {...externalProps} />
        <UnderlineButton {...externalProps} />
        <CodeButton {...externalProps} />
      </React.Fragment>
    ),
  };

  toolbar: HTMLDivElement | undefined = undefined;

  state = {
    isVisible: false,
    position: undefined,
    isLinkSelected: false,
    url: '',
    isImageSelected: false,
    isVideoSelected: false,
    isButtonSelected: false,
    isOpeningButtonForm: false, // Using `isOpeningButtonForm` to prevent re-caculate position of popup when focus on form
    anchorKey: undefined,
    /**
     * If this is set, the toolbar will render this instead of the children
     * prop and will also be shown when the editor loses focus.
     * @type {Component}
     */
    overrideContent: undefined,
  };

  UNSAFE_componentWillMount() {
    this.props.store.subscribeToItem('selection', this.onSelectionChanged);
  }

  componentWillUnmount() {
    this.props.store.unsubscribeFromItem('selection', this.onSelectionChanged);
  }

  /**
   * This can be called by a child in order to render custom content instead
   * of the children prop. It's the responsibility of the callee to call
   * this function again with `undefined` in order to reset `overrideContent`.
   * @param {Component} overrideContent
   */
  onOverrideContent = (overrideContent: any) => {
    this.setState({ overrideContent });
  };

  onChangeButtonType = (type: string) => {
    if (this.state.position) {
      this.setState({
        position: {
          //@ts-ignore
          top: this.state.position.top + (type === 'Register' ? 40 : -40), //@ts-ignore
          left: this.state.position.left,
        },
      });
    }
  };

  onSelectionChanged = () => {
    // need to wait a tick for window.getSelection() to be accurate
    // when focusing editor with already present selection
    setTimeout(() => {
      if (!this.toolbar) return;

      // The editor root should be two levels above the node from
      // `getEditorRef`. In case this changes in the future, we
      // attempt to find the node dynamically by traversing upwards.

      const getEditorRef = this.props.store.getItem('getEditorRef');
      if (!getEditorRef) return;
      const editorRef = getEditorRef();
      if (!editorRef) return;

      const getEditorState = this.props.store.getItem('getEditorState');
      if (!getEditorState) return;

      const hasLinkSelected = EditorUtils.hasEntity(getEditorState(), ENTITY_TYPE.LINK);
      const hasImageSelected = EditorUtils.hasEntity(getEditorState(), ENTITY_TYPE.IMAGE);
      const hasButtonSelected = EditorUtils.hasEntity(getEditorState(), ENTITY_TYPE.BUTTON);

      const linkInstance = EditorUtils.getCurrentEntity(getEditorState());

      const url = linkInstance ? linkInstance.getData().url : '';
      const hasBombBombVideoSelected = isBomBombVideoSelected(getEditorState());

      this.setState({
        isLinkSelected: hasLinkSelected || false,
        url,
        isImageSelected: hasImageSelected && !hasBombBombVideoSelected,
        isVideoSelected: hasImageSelected && hasBombBombVideoSelected,
        isButtonSelected: hasButtonSelected,
      });

      // This keeps backwards compatibility with React 15
      let editorRoot =
        editorRef.refs && editorRef.refs.editor ? editorRef.refs.editor : editorRef.editor;
      while (editorRoot.className.indexOf('DraftEditor-root') === -1) {
        editorRoot = editorRoot.parentNode;
      }
      const editorRootRect = editorRoot.getBoundingClientRect();

      const parentWindow = editorRoot.ownerDocument && editorRoot.ownerDocument.defaultView;
      const selectionRect = getVisibleSelectionRect(parentWindow || window);

      if (this.state.isButtonSelected || this.state.isImageSelected || this.state.isVideoSelected) {
        if (
          this.state.overrideContent ||
          (this.state.isButtonSelected &&
            this.state.isOpeningButtonForm &&
            this.state.anchorKey &&
            this.state.anchorKey === getEditorState().getSelection().getAnchorKey())
        )
          return;

        const selection = document.querySelector(
          '[data-offset-key^="' + getEditorState().getSelection().getAnchorKey() + '"]'
        )?.firstChild as Element | undefined;
        const boundingRect = selection?.getBoundingClientRect();
        if (!boundingRect) return;
        const relativeParent = getRelativeParent(this.toolbar.parentElement);
        const toolbarHeight = this.toolbar.clientHeight;
        const relativeRect = relativeParent
          ? relativeParent.getBoundingClientRect()
          : document.body.getBoundingClientRect();
        let minTop = editorRoot.offsetTop - toolbarHeight;
        if (this.props.isInsideModal) {
          const modalBody = this.toolbar.closest('.modal-body') as HTMLDivElement | null;
          if (modalBody) {
            minTop -= modalBody.scrollTop
          }
        };
        const newPosition = {
          top: Math.max(minTop, boundingRect.top - relativeRect.top - toolbarHeight),
          left: boundingRect.left - relativeRect.left + boundingRect.width / 2,
        };

        if (this.state.isImageSelected || this.state.isVideoSelected) {
          this.setState({
            position: {
              ...newPosition,
              top: newPosition.top - 10,
            },
          });
        }

        if (this.state.isButtonSelected) {
          this.setState({
            position: {
              ...newPosition,
              top: newPosition.top + 2,
            },
            isOpeningButtonForm: true,
            anchorKey: getEditorState().getSelection().getAnchorKey(),
          });
        } else {
          this.setState({
            isOpeningButtonForm: false,
            anchorKey: undefined,
          });
        }
        return;
      } else {
        this.setState({
          isOpeningButtonForm: false,
          anchorKey: undefined,
        });
      }
      // Do not cacl position if the entity is an IMAGE or BUTTON
      if (!selectionRect || this.state.isButtonSelected || this.state.isImageSelected) return;

      // The toolbar shouldn't be positioned directly on top of the selected text,
      // but rather with a small offset so the caret doesn't overlap with the text.
      const extraTopOffset = -5;
      const toolbarHeight = this.toolbar?.offsetHeight && Number(this.toolbar?.offsetHeight) > 30 ? this.toolbar.offsetHeight : 50;
      let minTop = editorRoot.offsetTop - toolbarHeight;
      let top = selectionRect.top - editorRootRect.top + editorRoot.offsetTop - toolbarHeight + extraTopOffset;
      
      if (this.props.isInsideModal) {
        const modalBody = this.toolbar.closest('.modal-body') as HTMLDivElement | null;
        if (modalBody) {
          top -= modalBody.scrollTop;
          minTop -= modalBody.scrollTop
        }
      };
      const position = {
        top: Math.max(minTop, top),
        left:
          editorRoot.offsetLeft +
          (selectionRect.left - editorRootRect.left) +
          selectionRect.width / 2,
      };

      this.setState({ position });
    }, 300);
  };

  getStyle() {
    const { store, isKeyPressing, linkToolbarDisabled } = this.props;
    const {
      overrideContent,
      position,
      isLinkSelected,
      isImageSelected,
      isVideoSelected,
      isButtonSelected,
    } = this.state;
    const selection = store.getItem('getEditorState')().getSelection();

    // overrideContent could for example contain a text input, hence we always show overrideContent
    // To check for readonly mode and possibly set isVisible to false if the editor is readonly
    const isVisible =
      (!selection.isCollapsed() && selection.getHasFocus()) ||
      overrideContent ||
      (!isKeyPressing &&
        (isLinkSelected || isImageSelected || isVideoSelected || isButtonSelected));

    const style = {
      // @ts-ignore
      ...position, // @ts-ignore
      top: isImageSelected && this.state.overrideContent ? position?.top + 26 : position?.top, // fix position of URL Input when adding link fo image
      minWidth:
        !isLinkSelected && !isImageSelected && !isButtonSelected && !isVideoSelected
          ? linkToolbarDisabled
            ? '8rem'
            : '10.45rem'
          : undefined,
    };
    if (isVisible) {
      style.visibility = 'visible';
      style.transform = 'translate(-50%) scale(1)';
      style.transition = 'transform 0.15s cubic-bezier(.3,1.2,.2,1)';
    } else {
      style.transform = 'translate(-50%) scale(0)';
      style.visibility = 'hidden';
    }

    return style;
  }

  handleToolbarRef = (node: any) => {
    this.toolbar = node;
  };

  render() {
    const { theme, store, onlyCustomCta, editorId, ...rest } = this.props;

    if (!store || !store.getItem('getEditorState')) return null;

    const {
      overrideContent,
      isLinkSelected,
      url,
      isImageSelected,
      isButtonSelected,
      isVideoSelected,
    } = this.state;
    const OverrideContent = overrideContent;
    const childrenProps = {
      ...rest,
      theme: theme.buttonStyles,
      getEditorState: store.getItem('getEditorState'),
      setEditorState: store.getItem('setEditorState'),
      onOverrideContent: this.onOverrideContent,
    };

    const selection = store.getItem('getEditorState')().getSelection();
    const isOtherToolbarVisible = !selection.isCollapsed() && selection.getHasFocus();

    return (
      <div
        id={editorId && `inlineToolbar-${editorId}`}
        key={selection.getAnchorKey()}
        css={theme && theme.toolbarStyles && theme.toolbarStyles.toolbar}
        style={this.getStyle()}
        ref={this.handleToolbarRef}
      >
        {OverrideContent ? ( //@ts-ignore
          <OverrideContent {...childrenProps} />
        ) : isOtherToolbarVisible && !isButtonSelected ? (
          this.props.children(childrenProps)
        ) : isLinkSelected ? (
          <AddLinkForm
            {...childrenProps} //@ts-ignore
            value={url}
            placeholder='Enter a URL and press enter'
          />
        ) : isVideoSelected ? (
          <BombBombToolbar {...childrenProps} />
        ) : isImageSelected ? (
          <ImageToolbar {...childrenProps} />
        ) : isButtonSelected ? ( //@ts-ignore
          <ButtonBlockForm
            {...childrenProps}
            onChangeButtonType={this.onChangeButtonType}
            onlyCustomCta={onlyCustomCta}
          />
        ) : null}
      </div>
    );
  }
}
